/**
 * GA4 Core Initialization & Script Loader
 * Configures Measurement ID from import.meta.env.VITE_GA_MEASUREMENT_ID
 */

import { getInitialConsentConfig } from './consent';

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

let isInitialized = false;

/**
 * Returns the configured Measurement ID from Vite environment variables.
 */
export function getMeasurementId(): string {
  const envId = (import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim();
  return envId;
}

/**
 * Checks whether GA4 is properly configured with a valid Measurement ID (e.g. G-XXXXXXXXXX).
 */
export function isGA4Configured(): boolean {
  const id = getMeasurementId();
  return Boolean(id && /^G-[A-Z0-9]+$/i.test(id) && id !== 'G-XXXXXXXXXX');
}

/**
 * Initializes Google Analytics 4:
 * 1. Configures dataLayer and gtag stub
 * 2. Applies default consent mode
 * 3. Dynamically injects gtag.js script
 * 4. Configures measurement property with send_page_view disabled for SPA control
 */
export function initGA4(): boolean {
  if (typeof window === 'undefined') return false;
  if (isInitialized) return true;

  const measurementId = getMeasurementId();

  // Initialize dataLayer and gtag helper regardless of ID so calls won't crash
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  // 1. Configure Consent Mode BEFORE gtag('js') and gtag('config')
  const initialConsent = getInitialConsentConfig();
  window.gtag('consent', 'default', initialConsent);

  // 2. Base initialization
  window.gtag('js', new Date());

  // If no valid Measurement ID is configured, exit gracefully with warning in dev
  if (!isGA4Configured()) {
    if (import.meta.env.DEV) {
      console.info(
        `[GA4 Analytics] Running in development or unconfigured mode. Set VITE_GA_MEASUREMENT_ID="G-XXXXXXXXXX" in .env to activate real tracking.`
      );
    }
    isInitialized = true;
    return false;
  }

  // 3. Inject GA4 script tag asynchronously if not already on the page
  const scriptId = 'google-tag-manager-ga4';
  if (!document.getElementById(scriptId)) {
    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }

  // 4. Configure measurement ID
  // Note: send_page_view: false ensures we have 100% control over SPA route changes and avoid duplicate page_views
  const isDebug =
    import.meta.env.DEV ||
    (typeof window !== 'undefined' && window.location.search.includes('debug_ga=true'));

  window.gtag('config', measurementId, {
    send_page_view: false,
    debug_mode: isDebug,
    cookie_flags: 'SameSite=None;Secure',
  });

  isInitialized = true;
  return true;
}

/**
 * Safely forwards command to window.gtag
 */
export function rawGtag(command: string, ...args: any[]): void {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(command, ...args);
  }
}
